import { useSyncExternalStore } from "react";
import { ApiError } from "./api";

/**
 * A tiny in-memory outbox for server writes.
 *
 * The report page fires each field write straight at the server; on a flaky
 * connection those writes used to fail silently and the typed data was lost
 * (Frøy Saga incident). This queue makes a *network* failure non-fatal: the
 * write stays pending and is retried (with backoff, and immediately on
 * reconnect) until it lands, so the caller's `await` simply resolves late
 * instead of dropping the change. A reachable server that *rejects* a write
 * (validation/permission) is a real error and is surfaced to the caller
 * unchanged — retrying it would never help.
 *
 * This is the "Tier 1" safety net from FANL-1398: it survives connection blips
 * while the page stays open. It is NOT durable across a reload/crash (no local
 * storage yet) — that's Tier 2.
 */

export type WriteEntry = {
  id: number;
  label: string;
  attempts: number;
};

/** How the latest write ended, for the saved/not-saved status the crew can see. */
type WriteOutcome = { ok: boolean; at: number; message: string | null };

type Listener = () => void;

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 15_000;

let nextId = 1;
const entries = new Map<number, WriteEntry>();
const listeners = new Set<Listener>();
let snapshot: WriteEntry[] = [];
let lastOutcome: WriteOutcome | null = null;

function emit() {
  snapshot = Array.from(entries.values());
  for (const listener of listeners) listener();
}

// Retries that are backing off register a waker so a reconnect can flush the
// whole queue immediately instead of waiting out each backoff.
let wakers: Array<() => void> = [];
function wakeAll() {
  const current = wakers;
  wakers = [];
  for (const wake of current) wake();
}
if (typeof window !== "undefined") {
  window.addEventListener("online", wakeAll);
}

function waitBeforeRetry(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const wake = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      wakers = wakers.filter((w) => w !== wake);
      resolve();
    }, ms);
    wakers.push(wake);
  });
}

function isNetworkError(error: unknown): boolean {
  // PocketBase wraps fetch failures and aborts/timeouts as a ApiError
  // with status 0. A reachable server that returns an HTTP status is NOT a
  // network error — that's a genuine rejection we must not retry.
  if (error instanceof ApiError) return error.status === 0;
  return error instanceof TypeError; // raw "Failed to fetch"
}

/**
 * Runs a server write with durability + retry.
 *
 * Resolves with the result once delivered. On a network failure it keeps the
 * write pending and retries until it lands. On an HTTP error it rejects
 * immediately so the caller can show its normal error.
 */
export async function runWrite<T>(label: string, run: () => Promise<T>): Promise<T> {
  const id = nextId++;
  const entry: WriteEntry = { id, label, attempts: 0 };
  entries.set(id, entry);
  emit();

  try {
    for (let attempt = 0; ; attempt++) {
      try {
        entry.attempts = attempt + 1;
        emit();
        return await run();
      } catch (error) {
        if (!isNetworkError(error)) throw error;
        const backoff = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
        await waitBeforeRetry(backoff);
      }
    }
  } finally {
    entries.delete(id);
    emit();
  }
}

/** Records how a write ended; a rejected one keeps the server's reason. */
export function noteWriteOutcome(ok: boolean, message: string | null) {
  lastOutcome = { ok, at: Date.now(), message };
  emit();
}

/** Forgets the last outcome, so a newly opened report starts without another one's status. */
export function clearWriteOutcome() {
  lastOutcome = null;
  emit();
}

/**
 * Shows a write as pending without retrying it, for creates where a retry after a lost response
 * could make a second record.
 */
export async function trackWrite<T>(label: string, run: () => Promise<T>): Promise<T> {
  const id = nextId++;
  entries.set(id, { id, label, attempts: 1 });
  emit();
  try {
    return await run();
  } finally {
    entries.delete(id);
    emit();
  }
}

export function subscribeWriteQueue(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getWriteQueueSnapshot(): WriteEntry[] {
  return snapshot;
}

/** Non-reactive read, for use inside event handlers (e.g. the submit guard). */
export function hasPendingWrites(): boolean {
  return entries.size > 0;
}

const getLastOutcome = () => lastOutcome;

export function useLastWriteOutcome(): WriteOutcome | null {
  return useSyncExternalStore(subscribeWriteQueue, getLastOutcome, getLastOutcome);
}

export function useWriteQueue(): { entries: WriteEntry[]; pendingCount: number; hasPending: boolean } {
  const items = useSyncExternalStore(subscribeWriteQueue, getWriteQueueSnapshot, getWriteQueueSnapshot);
  return { entries: items, pendingCount: items.length, hasPending: items.length > 0 };
}
